import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
  TooltipItem,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/axiosInstance';
import {
  format,
  startOfWeek,
  eachDayOfInterval,
  eachMonthOfInterval,
  isWithinInterval,
  differenceInCalendarDays,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import { BlockCounted } from '../blockCounted';
import { Box } from '@mui/material';
import { PeriodType } from '../periodToggle';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type Op = { id: number; amount: number; name: string; date: string };

interface ChartBarCategoryProps {
  period: PeriodType;
  type: 'income' | 'expense';
  categoryId: number;
  title?: string;
  from?: string;
  to?: string;
}

export const ChartBarCategory: React.FC<ChartBarCategoryProps> = ({
  period,
  type,
  categoryId,
  title,
  from,
  to,
}) => {
  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      categoryId,
    };

    if (period === 'custom') {
      if (from) params.from = from;
      if (to) params.to = to;
    }

    return params;
  }, [categoryId, period, from, to]);

  const { data: ops = [] } = useQuery<Op[]>({
    queryKey: ['category-ops', type, categoryId, period, from, to],
    queryFn: async () => {
      const endpoint = type === 'income' ? '/incomes/by-category' : '/expenses/by-category';

      const res = await api.get(endpoint, {
        params: queryParams,
      });

      return res.data;
    },
  });

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const today = now.getDate();

  const { labels, values } = useMemo((): { labels: string[]; values: number[] } => {
    const lbls: string[] = [];
    const vals: number[] = [];

    if (period === 'years') {
      const years = [currentYear - 1, currentYear];

      years.forEach((y, i) => {
        lbls.push(String(y));
        vals[i] = 0;
      });

      ops.forEach(o => {
        const d = new Date(o.date);
        const idx = years.indexOf(d.getFullYear());
        if (idx >= 0) vals[idx] += Number(o.amount);
      });

      return { labels: lbls, values: vals };
    }

    if (period === 'year') {
      for (let m = 0; m <= currentMonth; m++) {
        lbls.push(format(new Date(currentYear, m, 1), 'LLLL', { locale: ru }));
        vals[m] = 0;
      }

      ops.forEach(o => {
        const d = new Date(o.date);
        if (d.getFullYear() !== currentYear) return;

        const m = d.getMonth();
        if (m <= currentMonth) vals[m] += Number(o.amount);
      });

      return { labels: lbls, values: vals };
    }

    if (period === 'month') {
      const weeks = Math.ceil(today / 7);

      for (let i = 0; i < weeks; i++) {
        const start = i * 7 + 1;
        const end = Math.min((i + 1) * 7, today);
        lbls.push(`${start}-${end} ${format(now, 'LLLL', { locale: ru })}`);
        vals[i] = 0;
      }

      ops.forEach(o => {
        const d = new Date(o.date);
        if (d.getFullYear() !== currentYear) return;
        if (d.getMonth() !== currentMonth) return;
        if (d.getDate() > today) return;

        const idx = Math.floor((d.getDate() - 1) / 7);
        if (idx >= 0 && idx < vals.length) vals[idx] += Number(o.amount);
      });

      return { labels: lbls, values: vals };
    }

    if (period === 'week') {
      const weekStart = startOfWeek(now, { locale: ru, weekStartsOn: 1 });
      const days = eachDayOfInterval({ start: weekStart, end: now });

      days.forEach((d, i) => {
        lbls.push(format(d, 'EEEEEE', { locale: ru }));
        vals[i] = 0;
      });

      ops.forEach(o => {
        const d = new Date(o.date);
        const idx = days.findIndex(x => x.toDateString() === d.toDateString());
        if (idx >= 0) vals[idx] += Number(o.amount);
      });

      return { labels: lbls, values: vals };
    }

    if (period === 'custom' && from && to) {
      const start = new Date(from);
      const end = new Date(to);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
        return { labels: [], values: [] };
      }

      end.setHours(23, 59, 59, 999);

      const totalDays = differenceInCalendarDays(end, start) + 1;

      if (totalDays <= 31) {
        const days = eachDayOfInterval({ start, end });

        days.forEach((day, i) => {
          lbls.push(format(day, 'dd.MM', { locale: ru }));
          vals[i] = 0;
        });

        ops.forEach(o => {
          const d = new Date(o.date);
          if (!isWithinInterval(d, { start, end })) return;

          const idx = days.findIndex(x => x.toDateString() === d.toDateString());
          if (idx >= 0) vals[idx] += Number(o.amount);
        });

        return { labels: lbls, values: vals };
      }

      if (totalDays <= 120) {
        const days = eachDayOfInterval({ start, end });
        const weekMap = new Map<string, number>();

        days.forEach(day => {
          const weekStart = startOfWeek(day, { weekStartsOn: 1 });
          const key = format(weekStart, 'dd.MM.yyyy');

          if (!weekMap.has(key)) {
            weekMap.set(key, weekMap.size);
            lbls.push(`${format(weekStart, 'dd.MM')} нед.`);
            vals.push(0);
          }
        });

        ops.forEach(o => {
          const d = new Date(o.date);
          if (!isWithinInterval(d, { start, end })) return;

          const weekStart = startOfWeek(d, { weekStartsOn: 1 });
          const key = format(weekStart, 'dd.MM.yyyy');
          const idx = weekMap.get(key);

          if (idx !== undefined) vals[idx] += Number(o.amount);
        });

        return { labels: lbls, values: vals };
      }

      const months = eachMonthOfInterval({ start, end });

      months.forEach((month, i) => {
        lbls.push(format(month, 'LLLL yyyy', { locale: ru }));
        vals[i] = 0;
      });

      ops.forEach(o => {
        const d = new Date(o.date);
        if (!isWithinInterval(d, { start, end })) return;

        const idx = months.findIndex(
          month => month.getFullYear() === d.getFullYear() && month.getMonth() === d.getMonth(),
        );

        if (idx >= 0) vals[idx] += Number(o.amount);
      });

      return { labels: lbls, values: vals };
    }

    return { labels: [], values: [] };
  }, [ops, period, currentYear, currentMonth, today, now, from, to]);

  const total = useMemo(() => values.reduce((a: number, b: number) => a + b, 0), [values]);

  const chartData: ChartData<'bar'> = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: type === 'income' ? 'Доходы' : 'Расходы',
          data: values,
          backgroundColor: type === 'income' ? 'rgba(28,172,0,0.5)' : 'rgba(255,0,0,0.5)',
        },
      ],
    }),
    [labels, values, type],
  );

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: { display: true, text: title ?? 'Диаграмма по категории' },
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx: TooltipItem<'bar'>) => {
              const v = Number(ctx.parsed.y || 0);
              const percent = total > 0 ? Math.round((v / total) * 100) : 0;
              return `${ctx.dataset.label}: ${v} ₽ (${percent}%)`;
            },
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text:
              period === 'years'
                ? 'Год'
                : period === 'year'
                  ? 'Месяц'
                  : period === 'month'
                    ? 'Неделя'
                    : period === 'week'
                      ? 'День недели'
                      : 'Выбранный период',
          },
        },
        y: {
          title: { display: true, text: 'Сумма, ₽' },
          beginAtZero: true,
        },
      },
    }),
    [period, title, total],
  );

  const hasData = total > 0;

  const incomeDataForBlock = type === 'income' ? values : [];
  const expenseDataForBlock = type === 'expense' ? values : [];

  return (
    <div>
      <BlockCounted type={type} incomeData={incomeDataForBlock} expenseData={expenseDataForBlock} />

      {hasData ? (
        <Box sx={{ width: '100%', height: { xs: 320, sm: 420, md: 520 } }}>
          <Bar options={options} data={chartData} />
        </Box>
      ) : (
        <div>Нет записей для построения графики</div>
      )}
    </div>
  );
};
